import { useMemo } from 'react';
import { usePlacementContext } from '@post-office/placement-context';
import { useSubscriptionConfig } from '@post-office/placement-contexts/create-placement-component/providers/stable-graphql-subscription';
import { usePlacementSubscription } from '@atlassian/post-office-use-subscription';
import type { PlacementSubscriptionResult } from '@atlassian/post-office-use-subscription/types';
import { type SerializableRecord } from '@post-office/serializable-record';
import FeatureGates from '@atlaskit/feature-gate-js-client';

// https://switcheroo.atlassian.com/ui/gates/aae56c99-ea09-49e2-ab65-b1ec8618999d/key/post-office_enable_subscription-enrichment
export const FG_MESSAGE_ENABLE_ENRICHMENT = 'post-office_enable_subscription-enrichment';

/**
 * Creates the placement messages subscription document with the given data fragments.
 * Each placement defines and passes the GraphQL fragments it needs for the `data` field
 * of PostOfficeMessage, allowing placements to request only the fields they use.
 *
 * @param dataFragments - Array of GraphQL inline fragment strings for PostOfficeMessageData union members
 * @param options - Optional configuration for the subscription document
 * @param options.includeActiveScopes - When true, includes `activeScopes` variable declaration and argument
 * @param options.includePathname - When true, includes `pathname` variable declaration and argument
 * @param options.includeContext - When true, includes `context: PostOfficeContextInput` variable declaration and argument (carries tenantId, orgId, etc.)
 * @returns The full subscription document string
 */
export const createPlacementMessagesSubscription = (
	dataFragments: string[],
	options?: { includeActiveScopes?: boolean; includePathname?: boolean; includeContext?: boolean },
): string => {
	const subscriptionName = FeatureGates.checkGate(FG_MESSAGE_ENABLE_ENRICHMENT)
		? 'postOffice_onMessageReceivedEnriched'
		: 'postOffice_onMessageReceived';

	const variableDeclarations = [
		'$placementId: String!',
		'$product: String',
		'$workspaceAri: String',
		...(options?.includeActiveScopes ? ['$activeScopes: String'] : []),
		...(options?.includePathname ? ['$pathname: String'] : []),
		...(options?.includeContext ? ['$context: PostOfficeContextInput'] : []),
	];
	const variableDeclarationsBlock = variableDeclarations.map((line) => `\t${line}`).join('\n');

	const subscriptionArgs = [
		'placementId: $placementId',
		'product: $product',
		'workspaceAri: $workspaceAri',
		...(options?.includeActiveScopes ? ['activeScopes: $activeScopes'] : []),
		...(options?.includePathname ? ['pathname: $pathname'] : []),
		...(options?.includeContext ? ['context: $context'] : []),
	];
	const subscriptionArgsBlock = subscriptionArgs.map((line) => `\t\t${line}`).join('\n');

	const fragmentsBlock = dataFragments.map((fragment) => `\t\t\t\t${fragment}`).join('\n');

	return `subscription PostOfficeOnMessageReceived(
${variableDeclarationsBlock}) {
	${subscriptionName}(
${subscriptionArgsBlock}) {
		messages {
			messageInstanceId
			messageTemplateId
			messageCategory
			createdAt
			eventTime
			triggerId
			messageCreationType
			recommendationSession {
				sessionId
				entityId
				itemFeatureSetId
			}
			data {
${fragmentsBlock}
			}
		}
	}
}`;
};

export type SubscriptionMessage<TData extends SerializableRecord> = {
	messageInstanceId: string;
	messageTemplateId: string;
	messageCategory: string;
	createdAt: string;
	eventTime: string;
	triggerId: string;
	messageCreationType: 'explicit' | 'implicit' | null;
	recommendationSession: {
		sessionId: string;
		entityId: string;
		itemFeatureSetId: string;
	} | null;
	data: TData | null;
};

type SubscriptionPayload<TData extends SerializableRecord> = {
	messages: SubscriptionMessage<TData>[];
};

export type PlacementMessagesSubscriptionData<TData extends SerializableRecord> = {
	postOffice_onMessageReceived?: SubscriptionPayload<TData>;
	postOffice_onMessageReceivedEnriched?: SubscriptionPayload<TData>;
};

/**
 * Extracts messages from the subscription data regardless of which subscription
 * field name is used. Placements should use this instead of reading a specific
 * response key directly.
 */
export const getSubscriptionMessages = <TData extends SerializableRecord>(
	data: PlacementMessagesSubscriptionData<TData> | null | undefined,
): SubscriptionMessage<TData>[] => {
	if (!data) {
		return [];
	}
	const payload = FeatureGates.checkGate(FG_MESSAGE_ENABLE_ENRICHMENT)
		? data['postOffice_onMessageReceivedEnriched']
		: data['postOffice_onMessageReceived'];
	return payload?.messages ?? [];
};

/**
 * Reusable hook for subscribing to real-time placement message updates.
 *
 * The subscription query is constructed via `createPlacementMessagesSubscription` using
 * the data fragments passed by each placement.
 * Products only need to provide the transport (adapter) — not the query itself.
 *
 * Contextual variables are constructed using the same pattern as `usePlacementApi`:
 * - `placementId` from `usePlacementContext()`
 * - `cloudId`, `workspaceAri`, `product` from Post Office context (merged automatically by `usePlacementSubscription`)
 * - Subscription enabled/disabled from `useSubscriptionConfig()` (set in `createPlacement({ options: { subscription: { enabled: true } } })`)
 *
 * Each adapter handles the query format internally:
 * - Relay adapter (`createUseSubscribeAdapter`): uses the raw string directly
 * - Apollo adapter (`createApolloAdapter`): parses the string into a DocumentNode
 *
 * Any placement that has `subscription: { enabled: true }` in its config and is rendered
 * within a product that provides a `StableSubscriptionAdapterProvider` can use this hook
 * to receive live message updates.
 *
 * @example
 * ```tsx
 * const LIVE_NUDGE_DATA_FRAGMENT = `... on PostOfficeMessageLiveNudgeDemoMessageData { message title }`;
 * const {
 * 		data: subscriptionData,
 *		error: subscriptionError,
 *		isLoading: subscriptionLoading,
 *		isEnabledForLiveMessages
 *	} = usePlacementGraphQLSubscriptionApi<PostOfficeMessageLiveNudgeDemoMessageData>({
 *		dataFragments: [LIVE_NUDGE_DATA_FRAGMENT],
 *	});
 *
 * if (subscriptionError) {
 *		throw subscriptionError;
 *	}
 *
 * if (isEnabledForLiveMessages && subscriptionData) {
 *   const messages = getSubscriptionMessages(subscriptionData);
 *   // Render live messages...
 * }
 * ```
 */
export type UsePlacementGraphQLSubscriptionApiOptions = {
	/**
	 * GraphQL inline fragment strings for PostOfficeMessageData union members.
	 * Each placement defines the fragments it needs for the `data` field of messages,
	 * so it only requests the fields it uses.
	 *
	 * @example
	 * ```ts
	 * const LIVE_NUDGE_DEMO_DATA_FRAGMENT = `... on PostOfficeMessageLiveNudgeDemoMessageData {
	 *   __typename
	 *   message
	 *   title
	 * }`;
	 * usePlacementGraphQLSubscriptionApi({ dataFragments: [LIVE_NUDGE_DEMO_DATA_FRAGMENT] });
	 * ```
	 */
	dataFragments: string[];
	/**
	 * Variables to pass to the subscription query, merged with the default variables
	 * (placementId, cloudId, workspaceAri, product) constructed by `usePlacementSubscription`.
	 *
	 * When `activeScopes` is provided (even if `null`), the subscription document will
	 * automatically include the `$activeScopes: String` variable
	 * declaration and pass it to the subscription operation.
	 *
	 * When variables include the `pathname` key (even if the value is `undefined` or `null`), the
	 * subscription document will automatically include the `$pathname: String` variable
	 * declaration and pass it to the subscription operation.
	 *
	 * When variables include the `context` key, the subscription document will automatically
	 * include the `$context: PostOfficeContextInput` variable declaration and pass it to the
	 * subscription operation. Use this to forward tenantId, orgId, subproduct, etc. to the
	 * backend so that `orchestrationContext.tenantId` is populated for implicit message creators.
	 *
	 * @example
	 * ```ts
	 * const activeScopes = useContextScope({ scopes, treatIntersectionAsSame: true });
	 * const { tenantId } = usePostOfficeContext();
	 *
	 * usePlacementGraphQLSubscriptionApi({
	 *   dataFragments: [LIVE_NUDGE_DEMO_DATA_FRAGMENT],
	 *   variables: {
	 *     activeScopes: activeScopes.current.length > 0 ? activeScopes.current : null,
	 *     pathname: window.location.pathname,
	 *     context: { tenantId },
	 *   },
	 * });
	 * ```
	 */
	variables?: {
		activeScopes?: string | null;
		pathname?: string;
		context?: {
			tenantId?: string;
			orgId?: string;
			product?: string;
			subproduct?: string;
			isSiteAdmin?: boolean;
			locale?: string;
			productEdition?: string;
		} | null;
	};
};

/**
 * Type parameter TData lets the placement specify which PostOfficeMessageData variant it subscribes to.
 * This narrows `msg.data` in the returned messages, so you get proper typing without manual casting.
 *
 * @example
 * ```tsx
 * usePlacementGraphQLSubscriptionApi<PostOfficeMessageLiveNudgeDemoMessageData>({
 *   dataFragments: [LIVE_NUDGE_FRAGMENT],
 * });
 * // msg.data is now PostOfficeMessageLiveNudgeDemoMessageData | null
 * ```
 */
const EMPTY_VARIABLES = {};

export const usePlacementGraphQLSubscriptionApi = <TData extends SerializableRecord>(
	options: UsePlacementGraphQLSubscriptionApiOptions,
): PlacementSubscriptionResult<PlacementMessagesSubscriptionData<TData>> => {
	const { placementId } = usePlacementContext();
	const subscriptionConfig = useSubscriptionConfig();
	const variables = options.variables ?? EMPTY_VARIABLES;
	const includeActiveScopes = 'activeScopes' in variables;
	const includePathname = 'pathname' in variables;
	const includeContext = 'context' in variables;
	const subscription = useMemo(
		() =>
			createPlacementMessagesSubscription(options.dataFragments, {
				includeActiveScopes,
				includePathname,
				includeContext,
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps -- subscription only changes when dataFragments content or optional variable flags change
		[options.dataFragments.join(), includeActiveScopes, includePathname, includeContext],
	);

	return usePlacementSubscription<PlacementMessagesSubscriptionData<TData>>({
		subscription,
		placementId: placementId ?? undefined,
		subscriptionSettings: subscriptionConfig,
		variables: options.variables,
	});
};
